import { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import * as Sentry from "@sentry/react";

import {
  Box,
  Grid,
  Typography,
  Switch,
  IconButton,
  Chip,
  CircularProgress,
  Autocomplete,
  TextField,
  Tooltip,
  useTheme
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";

import { calculateMergedProductFootprint } from "@aclymatepackages/products";

import GrayPaper from "../../../../atoms/GrayPaper";
import ProductPdfCapture from "./ProductPdfCapture";
import SharedReportOverlay from "../../../../modules/reporting/SharedReportOverlay";

import {
  useAccountData,
  useCachedDisplayData,
  useCachedFirebaseCrud
} from "../../../../../helpers/firebase";
import { useUpdateVendorRelationshipFields } from "../../../../../helpers/components/vendors";
import {
  isLocalOnlyMode,
  isProductOwnedBy
} from "../../../../../helpers/components/products";
import { fetchOurApi } from "../../../../../helpers/utils/apiCalls";
import useProductsEmissionsFactors from "../../../../../helpers/contexts/ProductsEmissionsFactors";
import usePlatformLayoutContext from "../../../../../helpers/contexts/platformLayout";

// One entry of a customer relationship's sharedProducts array.
const sharedEntryShape = PropTypes.shape({
  productId: PropTypes.string.isRequired,
  productName: PropTypes.string,
  bomShared: PropTypes.bool,
  reportShared: PropTypes.bool,
  bomStatus: PropTypes.string,
  reportStatus: PropTypes.string,
  reportUrl: PropTypes.string,
  isFinalized: PropTypes.bool
});

const productReportSessionKey = (vendorId, productId) =>
  `product-report-url-${vendorId}-${productId}`;

const ProductStatusAvatar = ({ isFinalized }) => {
  const { palette } = useTheme();
  const bg = isFinalized ? palette.secondary.main : palette.warning.main;
  const label = isFinalized ? "Finalized" : "Preliminary";
  const Icon = isFinalized ? CheckCircleIcon : WarningIcon;

  return (
    <Tooltip title={label}>
      <Box
        style={{
          backgroundColor: bg,
          width: 28,
          height: 28,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Icon style={{ fontSize: 16, color: "white" }} />
      </Box>
    </Tooltip>
  );
};
ProductStatusAvatar.propTypes = {
  isFinalized: PropTypes.bool
};

const reviewStatusChip = (status) => {
  if (status === "reviewed") {
    return <Chip label="Reviewed" color="success" size="small" />;
  }
  if (status === "shared") {
    return <Chip label="Pending Review" color="warning" size="small" />;
  }
  return <Chip label="Not Shared" variant="outlined" size="small" />;
};

const ShareTypeRow = ({ label, isShared, status, onToggle, isLoading }) => (
  <Grid container alignItems="center" spacing={1}>
    <Grid item xs>
      <Typography variant="body2">{label}</Typography>
    </Grid>
    <Grid item>{reviewStatusChip(isShared ? status : null)}</Grid>
    <Grid item>
      {isLoading ? (
        <Box px={1.5}>
          <CircularProgress size={16} />
        </Box>
      ) : (
        <Switch checked={isShared} onChange={onToggle} color="primary" />
      )}
    </Grid>
  </Grid>
);
ShareTypeRow.propTypes = {
  label: PropTypes.string.isRequired,
  isShared: PropTypes.bool,
  status: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  isLoading: PropTypes.bool
};

const SharedProductRow = ({
  product,
  sharedEntry,
  isFinalized,
  isReadOnly,
  onView,
  onUnshare,
  onToggleBom,
  onToggleReport,
  generatingReport
}) => {
  const { bomShared, reportShared, bomStatus, reportStatus, reportUrl } =
    sharedEntry;

  return (
    <Grid container direction="column" spacing={1}>
      <Grid item container alignItems="center" spacing={1} wrap="nowrap">
        <Grid item>
          <ProductStatusAvatar isFinalized={isFinalized} />
        </Grid>
        <Grid item xs>
          <Typography variant="body2">{product.name}</Typography>
          {product.sku && (
            <Typography variant="caption" color="textSecondary">
              {product.sku}
            </Typography>
          )}
        </Grid>
        {reportUrl && reportShared && (
          <Grid item>
            <Tooltip title="View report">
              <IconButton size="small" onClick={onView}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        {!isReadOnly && (
          <Grid item>
            <Tooltip title="Remove product">
              <IconButton size="small" color="error" onClick={onUnshare}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
      </Grid>
      {!isReadOnly && (
        <Grid item>
          <Grid container direction="column" spacing={0.5}>
            <Grid item>
              <ShareTypeRow
                label="BOM Data"
                isShared={bomShared}
                status={bomStatus}
                onToggle={onToggleBom}
              />
            </Grid>
            <Grid item>
              <ShareTypeRow
                label="Report"
                isShared={reportShared}
                status={reportStatus}
                onToggle={onToggleReport}
                isLoading={generatingReport}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
      {isReadOnly && (
        <Grid item>
          <Grid container direction="column" spacing={0.5}>
            {bomShared && (
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <Typography variant="body2" color="textSecondary">
                      BOM Data
                    </Typography>
                  </Grid>
                  <Grid item>{reviewStatusChip(bomStatus)}</Grid>
                </Grid>
              </Grid>
            )}
            {reportShared && (
              <Grid item>
                <Grid container alignItems="center" spacing={1}>
                  <Grid item xs>
                    <Typography variant="body2" color="textSecondary">
                      Report
                    </Typography>
                  </Grid>
                  <Grid item>{reviewStatusChip(reportStatus)}</Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

SharedProductRow.propTypes = {
  product: PropTypes.shape({
    name: PropTypes.string,
    sku: PropTypes.string
  }).isRequired,
  sharedEntry: sharedEntryShape.isRequired,
  isFinalized: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  onView: PropTypes.func.isRequired,
  onUnshare: PropTypes.func.isRequired,
  onToggleBom: PropTypes.func.isRequired,
  onToggleReport: PropTypes.func.isRequired,
  generatingReport: PropTypes.bool
};

const BuyerAssignedProductRow = ({ name, bomStatus }) => {
  const statusLabel =
    {
      requested: "BOM Requested",
      confirmed: "BOM Confirmed",
      pending: "Pending"
    }[bomStatus] || bomStatus;

  return (
    <Grid container alignItems="center" spacing={1} wrap="nowrap">
      <Grid item xs>
        <Typography variant="body2">{name}</Typography>
      </Grid>
      <Grid item>
        <Chip label={statusLabel} size="small" variant="outlined" />
      </Grid>
    </Grid>
  );
};

BuyerAssignedProductRow.propTypes = {
  name: PropTypes.string,
  bomStatus: PropTypes.string
};

const ProductsSection = ({
  customer,
  isReadOnly = false,
  onSharedProductsChange,
  buyerAssignedProducts = []
}) => {
  const [{ id: vendorAccountId }] = useAccountData();
  const [dbProducts] = useCachedDisplayData("product-components");
  const [productLifecycleEvents] = useCachedDisplayData(
    "product-lifecycle-events"
  );
  const [{ emissionsFactors }] = useProductsEmissionsFactors();
  const { updateCollectionDoc } = useCachedFirebaseCrud();
  const layoutContext = usePlatformLayoutContext() || {};
  const { activateSnackbar } = layoutContext;
  const updateVendorRelationshipFields =
    useUpdateVendorRelationshipFields("customers");

  const [generatingProductId, setGeneratingProductId] = useState(null);
  const [viewingProduct, setViewingProduct] = useState(null);
  const [selectorKey, setSelectorKey] = useState(0);
  const [optimisticSharedProducts, setOptimisticSharedProducts] =
    useState(null);
  const [trackedCustomerId, setTrackedCustomerId] = useState(customer.id);

  if (customer.id !== trackedCustomerId) {
    setTrackedCustomerId(customer.id);
    setOptimisticSharedProducts(null);
  }

  const { id: relationshipId, buyerId } = customer;
  const sharedProducts = useMemo(
    () => optimisticSharedProducts ?? (customer.sharedProducts || []),
    [optimisticSharedProducts, customer.sharedProducts]
  );

  const getLifecycleEvent = useCallback(
    (productId) =>
      productLifecycleEvents.find((e) => e.productId === productId) || {},
    [productLifecycleEvents]
  );

  const isProductFinalized = useCallback(
    (productId) => {
      const { bomStatus } = getLifecycleEvent(productId);
      return bomStatus === "confirmed";
    },
    [getLifecycleEvent]
  );

  const getMergedProduct = (productDoc) => {
    const lifecycleEvent = getLifecycleEvent(productDoc.id);
    return calculateMergedProductFootprint(
      productDoc,
      lifecycleEvent,
      emissionsFactors
    );
  };

  const getSharedEntry = (productId) =>
    sharedProducts.find((p) => p.productId === productId) || null;

  useEffect(() => {
    if (isReadOnly) {
      return;
    }
    const hasStale = sharedProducts.some(
      (p) => isProductFinalized(p.productId) && !p.isFinalized
    );
    if (!hasStale) {
      return;
    }
    const updatedProducts = sharedProducts.map((p) =>
      isProductFinalized(p.productId) && !p.isFinalized
        ? { ...p, isFinalized: true, reportUrl: null }
        : p
    );
    setOptimisticSharedProducts(updatedProducts);
    updateVendorRelationshipFields(relationshipId, {
      sharedProducts: updatedProducts
    });
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
  }, [
    productLifecycleEvents,
    sharedProducts,
    isReadOnly,
    relationshipId,
    isProductFinalized,
    onSharedProductsChange,
    updateVendorRelationshipFields
  ]);

  useEffect(() => {
    if (isReadOnly || generatingProductId) {
      return;
    }
    const needsRegen = sharedProducts.find(
      (p) => p.reportShared && !p.reportUrl
    );
    if (!needsRegen) {
      return;
    }
    const canonicalUrl =
      isProductFinalized(needsRegen.productId) && vendorAccountId
        ? sessionStorage.getItem(
            productReportSessionKey(vendorAccountId, needsRegen.productId)
          )
        : null;
    if (!canonicalUrl) {
      setGeneratingProductId(needsRegen.productId);
      return;
    }
    const updatedProducts = sharedProducts.map((p) =>
      p.productId === needsRegen.productId
        ? { ...p, reportUrl: canonicalUrl }
        : p
    );
    setOptimisticSharedProducts(updatedProducts);
    updateVendorRelationshipFields(relationshipId, {
      sharedProducts: updatedProducts
    });
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
  }, [
    sharedProducts,
    isReadOnly,
    generatingProductId,
    isProductFinalized,
    vendorAccountId,
    relationshipId,
    onSharedProductsChange,
    updateVendorRelationshipFields
  ]);

  // The share toggles only record intent on the relationship; the buyer's
  // actual read access is derived from that saved state by the sync below.
  // So a failed relationship write must stop here: syncing would "succeed"
  // against the old state and leave the buyer's access unchanged while the
  // optimistic toggles claim otherwise.
  const updateSharedProducts = async (updatedProducts) => {
    setOptimisticSharedProducts(updatedProducts);
    const result = await updateVendorRelationshipFields(relationshipId, {
      sharedProducts: updatedProducts
    });
    if (result?.error) {
      setOptimisticSharedProducts(null);
      activateSnackbar?.({
        message: `We couldn't save this change${
          result.message ? ` (${result.message})` : ""
        }. Please try again.`,
        severity: "error"
      });
      return false;
    }
    onSharedProductsChange && onSharedProductsChange(updatedProducts);
    return true;
  };

  const onShare = async ({ id: productId, name: productName }) => {
    const now = new Date();
    const productIsFinalized = isProductFinalized(productId);
    const updatedProducts = [
      ...sharedProducts,
      {
        productId,
        productName,
        sharedAt: now,
        bomShared: false,
        reportShared: false,
        bomStatus: "shared",
        reportStatus: "shared",
        isFinalized: productIsFinalized
      }
    ];
    if (!(await updateSharedProducts(updatedProducts))) {
      return;
    }
    await fetchOurApi({
      path: "/products/share-product-with-buyer",
      method: "POST",
      data: {
        productId,
        vendorId: vendorAccountId,
        buyerId,
        bomStatus: productIsFinalized ? "confirmed" : "shared"
      }
    });
  };

  const requestBuyerAccessSync = (productId) =>
    fetchOurApi({
      path: "/products/sync-product-buyer-access",
      method: "POST",
      data: { productId, buyerId }
    });

  const findProductName = (productId) =>
    dbProducts.find(({ id }) => id === productId)?.name ||
    getSharedEntry(productId)?.productName ||
    "this product";

  // The backend derives the buyer's read grant on the product and its
  // component docs from the sharedProducts entry just saved, so this must run
  // after updateSharedProducts succeeds. fetchOurApi resolves with { error }
  // rather than throwing, and a failed sync means the customer's real access
  // no longer matches the toggles, so it's retried once (the sync is
  // idempotent), then reported.
  // A customer who isn't on the platform yet has no buyerId, so there's no
  // one to grant; simulation/view modes keep every write local.
  const syncBuyerAccess = async (productId, retryHint) => {
    if (!buyerId || isLocalOnlyMode(layoutContext)) {
      return;
    }
    const firstAttempt = await requestBuyerAccessSync(productId);
    const result = firstAttempt?.error
      ? await requestBuyerAccessSync(productId)
      : firstAttempt;
    if (!result?.error) {
      return;
    }
    Sentry.captureException(new Error("Product buyer access sync failed"), {
      tags: { feature: "product-buyer-access-sync" },
      extra: { productId, message: result.message }
    });
    activateSnackbar?.({
      message: `We couldn't update this customer's access to ${findProductName(
        productId
      )}, so what they can see may not match these settings. ${retryHint}`,
      severity: "error"
    });
  };

  const TOGGLE_RETRY_HINT = "Toggle the share again to retry.";

  const onUnshare = async (productId) => {
    const productName = findProductName(productId);
    const updatedProducts = sharedProducts.filter(
      (p) => p.productId !== productId
    );
    if (!(await updateSharedProducts(updatedProducts))) {
      return;
    }
    await syncBuyerAccess(
      productId,
      `They may still have access to ${productName}. Add it back and remove it again to retry.`
    );
  };

  const copyLifecycleFieldsToProduct = async (productId) => {
    const { packaging, route, sku, packagingWeight } =
      getLifecycleEvent(productId);
    const lifecycleFields = {
      ...(packaging !== undefined ? { packaging } : {}),
      ...(route !== undefined ? { route } : {}),
      ...(sku !== undefined ? { sku } : {}),
      ...(packagingWeight !== undefined ? { packagingWeight } : {})
    };
    if (!Object.keys(lifecycleFields).length) {
      return;
    }
    await updateCollectionDoc("product-components", productId, lifecycleFields);
  };

  const onToggleBom = async (productId, enabled) => {
    const updatedProducts = sharedProducts.map((p) =>
      p.productId === productId ? { ...p, bomShared: enabled } : p
    );
    if (!(await updateSharedProducts(updatedProducts))) {
      return;
    }
    if (enabled) {
      await copyLifecycleFieldsToProduct(productId);
    }
    await syncBuyerAccess(productId, TOGGLE_RETRY_HINT);
  };

  const onToggleReport = async (productId, enabled) => {
    const entry = getSharedEntry(productId);
    if (!enabled) {
      const updatedProducts = sharedProducts.map((p) =>
        p.productId === productId ? { ...p, reportShared: false } : p
      );
      if (!(await updateSharedProducts(updatedProducts))) {
        return;
      }
      await syncBuyerAccess(productId, TOGGLE_RETRY_HINT);
      return;
    }
    const newIsFinalized = isProductFinalized(productId);
    if (entry?.reportUrl && newIsFinalized !== entry?.isFinalized) {
      const clearedProducts = sharedProducts.map((p) =>
        p.productId === productId ? { ...p, reportUrl: null } : p
      );
      if (!(await updateSharedProducts(clearedProducts))) {
        return;
      }
      setGeneratingProductId(productId);
      return;
    }
    if (entry?.reportUrl) {
      const productDoc = dbProducts.find((p) => p.id === productId);
      const mergedProduct = productDoc ? getMergedProduct(productDoc) : null;
      const updatedProducts = sharedProducts.map((p) =>
        p.productId === productId
          ? {
              ...p,
              reportShared: true,
              ...(mergedProduct?.kgsCo2e !== undefined
                ? { kgsCo2e: mergedProduct.kgsCo2e }
                : {}),
              ...(mergedProduct?.footprintBreakdownKgs !== undefined
                ? { footprintBreakdownKgs: mergedProduct.footprintBreakdownKgs }
                : {})
            }
          : p
      );
      if (!(await updateSharedProducts(updatedProducts))) {
        return;
      }
      await syncBuyerAccess(productId, TOGGLE_RETRY_HINT);
      return;
    }
    setGeneratingProductId(productId);
  };

  const onUploadComplete = async (
    productId,
    url,
    kgsCo2e,
    footprintBreakdownKgs
  ) => {
    const newIsFinalized = isProductFinalized(productId);

    if (newIsFinalized && vendorAccountId) {
      sessionStorage.setItem(
        productReportSessionKey(vendorAccountId, productId),
        url
      );
    }

    const updatedProducts = sharedProducts.map((p) => {
      if (p.productId !== productId) {
        return p;
      }
      const bomStatus =
        newIsFinalized && !p.isFinalized && p.bomStatus === "reviewed"
          ? "shared"
          : p.bomStatus;
      const reportStatus =
        newIsFinalized && !p.isFinalized && p.reportStatus === "reviewed"
          ? "shared"
          : p.reportStatus;
      return {
        ...p,
        reportUrl: url,
        reportShared: true,
        isFinalized: newIsFinalized,
        bomStatus,
        reportStatus,
        ...(kgsCo2e !== undefined ? { kgsCo2e } : {}),
        ...(footprintBreakdownKgs !== undefined
          ? { footprintBreakdownKgs }
          : {})
      };
    });

    const saved = await updateSharedProducts(updatedProducts);
    setGeneratingProductId(null);
    if (!saved) {
      return;
    }
    await syncBuyerAccess(productId, TOGGLE_RETRY_HINT);
  };

  const onUploadError = () => setGeneratingProductId(null);

  const sharedProductIds = sharedProducts.map((p) => p.productId);
  const unsharedProducts = dbProducts.filter((product) => {
    const { id } = product;
    if (sharedProductIds.includes(id)) {
      return false;
    }
    if (!isProductOwnedBy(product, vendorAccountId)) {
      return false;
    }
    const event = productLifecycleEvents.find((e) => e.productId === id);
    return !event?.bomStatus || event?.bomStatus === "confirmed";
  });

  const generatingProduct = generatingProductId
    ? dbProducts.find((p) => p.id === generatingProductId)
    : null;
  const mergedGeneratingProduct = generatingProduct
    ? getMergedProduct(generatingProduct)
    : null;

  return (
    <>
      {viewingProduct && (
        <SharedReportOverlay
          reportUrl={viewingProduct.reportUrl}
          title={`${viewingProduct.productName} Product Report`}
          isFinalized={isProductFinalized(viewingProduct.productId)}
          onClose={() => setViewingProduct(null)}
        />
      )}
      {mergedGeneratingProduct && (
        <ProductPdfCapture
          product={mergedGeneratingProduct}
          onUploaded={(url) =>
            onUploadComplete(
              generatingProductId,
              url,
              mergedGeneratingProduct.kgsCo2e,
              mergedGeneratingProduct.footprintBreakdownKgs
            )
          }
          onError={onUploadError}
        />
      )}
      <Grid container direction="column" spacing={2}>
        {buyerAssignedProducts.length > 0 && (
          <Grid item>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Products assigned by customer
            </Typography>
            <Grid container direction="column" spacing={1}>
              {buyerAssignedProducts.map(({ lifecycleId, name, bomStatus }) => (
                <Grid item key={lifecycleId}>
                  <GrayPaper style={{ padding: 12 }}>
                    <BuyerAssignedProductRow
                      name={name}
                      bomStatus={bomStatus}
                    />
                  </GrayPaper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
        {!isReadOnly && unsharedProducts.length > 0 && (
          <Grid item>
            <Autocomplete
              key={selectorKey}
              options={unsharedProducts}
              getOptionLabel={(p) => p.name || ""}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Add a product to share"
                  size="small"
                />
              )}
              onChange={(_, product) => {
                if (product) {
                  onShare(product);
                  setSelectorKey((prev) => prev + 1);
                }
              }}
              value={null}
            />
          </Grid>
        )}
        {sharedProductIds.map((productId) => {
          const productDoc = dbProducts.find((p) => p.id === productId);
          const sharedEntry = getSharedEntry(productId);

          if (!productDoc || !sharedEntry) {
            return null;
          }

          return (
            <Grid item key={productId}>
              <GrayPaper style={{ padding: 16 }}>
                <SharedProductRow
                  product={productDoc}
                  sharedEntry={sharedEntry}
                  isFinalized={isProductFinalized(productId)}
                  isReadOnly={isReadOnly}
                  onView={() =>
                    setViewingProduct({
                      productId,
                      reportUrl: sharedEntry.reportUrl,
                      productName: productDoc.name
                    })
                  }
                  onUnshare={() => onUnshare(productId)}
                  onToggleBom={() =>
                    onToggleBom(productId, !sharedEntry.bomShared)
                  }
                  onToggleReport={() =>
                    onToggleReport(productId, !sharedEntry.reportShared)
                  }
                  generatingReport={generatingProductId === productId}
                />
              </GrayPaper>
            </Grid>
          );
        })}
        {sharedProductIds.length === 0 && isReadOnly && (
          <Grid item>
            <Typography variant="body2" color="textSecondary" align="center">
              No products have been shared yet.
            </Typography>
          </Grid>
        )}
        {unsharedProducts.length === 0 &&
          sharedProductIds.length === 0 &&
          !isReadOnly && (
            <Grid item>
              <Typography variant="body2" color="textSecondary" align="center">
                No products available to share.
              </Typography>
            </Grid>
          )}
      </Grid>
    </>
  );
};

ProductsSection.propTypes = {
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    buyerId: PropTypes.string,
    sharedProducts: PropTypes.arrayOf(sharedEntryShape)
  }).isRequired,
  isReadOnly: PropTypes.bool,
  onSharedProductsChange: PropTypes.func,
  buyerAssignedProducts: PropTypes.arrayOf(
    PropTypes.shape({
      lifecycleId: PropTypes.string,
      name: PropTypes.string,
      bomStatus: PropTypes.string
    })
  )
};

export default ProductsSection;
